import { useQuery } from "@tanstack/react-query";
import { api } from "./client";

// Server types — wider than the prototype shapes so we can keep evolving the API
// without rewriting the screens. Each hook maps to the shape the screen expects.

export type AdminUserApi = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  lastSeen: string | null;
};

export type AdminClientApi = {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  status: string;
  plan: string;
  account: string;
  contractStart: string | null;
  brandLocked: boolean;
  seats: number;
  activeOrders: number;
  spendPence: number;
  updated: string;
  users: AdminUserApi[];
};

const ACCENT_BY_SLUG: Record<string, string> = {
  northshore: "#005EB8",
  riverside: "#00857C",
  acmestudio: "#D72027",
  westvale: "#6F4BB8",
  highland: "#2E6E4B",
  "rdi-internal": "#1A1A1A",
};

export function useAdminClients() {
  return useQuery({
    queryKey: ["admin", "clients"],
    queryFn: async () => {
      const { clients } = await api<{ clients: AdminClientApi[] }>("/api/v1/admin/clients");
      return clients.map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        domain: c.domain ?? "",
        accent: ACCENT_BY_SLUG[c.slug] ?? "#005EB8",
        logoText: c.name.split(" ").map((w) => w[0]).join("").slice(0, 4),
        status: c.status,
        plan: c.plan,
        account: c.account,
        contractStart: c.contractStart ?? "—",
        primaryContact: c.users[0]?.name ?? "—",
        primaryEmail: c.users[0]?.email ?? "—",
        brandLocked: c.brandLocked,
        seats: c.seats,
        activeOrders: c.activeOrders,
        spend: c.spendPence / 100,
        updated: c.updated,
        users: c.users.map((u) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          status: u.status,
          lastSeen: u.lastSeen
            ? new Date(u.lastSeen).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit" })
            : "—",
        })),
      }));
    },
  });
}

export type AdminKitApi = {
  id: string;
  code: string;
  title: string;
  description: string;
  tube: string;
  tat: string;
  pricePence: number;
  active: boolean;
  stockQty: number;
  tag: string | null;
  tiers: number[];
  visibleTo: string[];
};

export function useAdminCatalogue() {
  return useQuery({
    queryKey: ["admin", "catalogue"],
    queryFn: async () => {
      const { kits } = await api<{ kits: AdminKitApi[] }>("/api/v1/admin/catalogue");
      return kits.map((k) => ({
        ...k,
        desc: k.description,
        price: k.pricePence / 100,
      }));
    },
  });
}

export type AdminOrderApi = {
  id: string;
  clientId: string;
  clientName: string;
  kitCode: string;
  kitTitle: string;
  qty: number;
  totalPence: number;
  status: string;
  placedAt: string;
  placedBy: string;
  holdReason: string | null;
  royalMailTracking: string | null;
  factoryReceivedAt: string | null;
  packedAt: string | null;
  dispatchedAt: string | null;
};

export function useAdminOrders() {
  return useQuery({
    queryKey: ["admin", "orders"],
    queryFn: async () => {
      const { orders } = await api<{ orders: AdminOrderApi[] }>("/api/v1/admin/orders");
      return orders.map((o) => ({
        id: o.id,
        clientId: o.clientId,
        kit: o.kitCode,
        kitTitle: o.kitTitle,
        qty: o.qty,
        total: o.totalPence / 100,
        status: o.status,
        placed: new Date(o.placedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
        proofBy: o.placedBy,
        holdReason: o.holdReason ?? undefined,
        royalMailTracking: o.royalMailTracking ?? null,
      }));
    },
  });
}

export type AuditEntryApi = {
  id: string;
  ts: string;
  actor: { name: string; role: string; uid: string | null };
  tenantId: string | null;
  severity: string;
  action: string;
  target: string;
  detail: string;
  hash: string;
};

export function useAdminAudit() {
  return useQuery({
    queryKey: ["admin", "audit"],
    queryFn: async () => {
      const { entries } = await api<{ entries: AuditEntryApi[] }>("/api/v1/admin/audit");
      return entries.map((e) => ({
        id: e.id,
        ts: new Date(e.ts).toISOString().slice(0, 16).replace("T", " "),
        actor: { name: e.actor.name, role: e.actor.role },
        severity: e.severity as "info" | "warn" | "crit",
        what: e.action,
        target: e.target,
        detail: e.detail,
      }));
    },
  });
}
